/**
 * Use Case Cost Breakdown Component
 * Hierarchical tree view showing parent use cases with expandable sub-steps
 */

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { UseCaseCostItem, USECASE_LABELS } from '../types';

interface UseCaseCostBreakdownProps {
  data: UseCaseCostItem[];
}

interface GroupedUseCase {
  parent: string;
  totalCost: number;
  totalQueries: number;
  subSteps: UseCaseCostItem[];
}

const BRAND_PRIMARY = '#03e8d3';
const INITIAL_DISPLAY_COUNT = 5;

export const UseCaseCostBreakdown: React.FC<UseCaseCostBreakdownProps> = ({ data }) => {
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  const formatCost = (value: number): string => {
    if (value >= 1) {
      return `$${value.toFixed(2)}`;
    }
    if (value >= 0.01) {
      return `$${value.toFixed(3)}`;
    }
    return `$${value.toFixed(4)}`;
  };

  const formatNumber = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  const formatUseCaseName = (name: string): string => {
    return USECASE_LABELS[name] || name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const groupedData = useMemo(() => {
    const groups: Record<string, GroupedUseCase> = {};

    data.forEach((item) => {
      const parent = item.parent_usecase;
      if (!groups[parent]) {
        groups[parent] = {
          parent,
          totalCost: 0,
          totalQueries: 0,
          subSteps: [],
        };
      }
      groups[parent].totalCost += item.total_cost;
      groups[parent].totalQueries += item.query_count;
      groups[parent].subSteps.push(item);
    });

    return Object.values(groups).sort((a, b) => b.totalCost - a.totalCost);
  }, [data]);

  const toggleExpand = (parent: string) => {
    const newExpanded = new Set(expandedParents);
    if (newExpanded.has(parent)) {
      newExpanded.delete(parent);
    } else {
      newExpanded.add(parent);
    }
    setExpandedParents(newExpanded);
  };

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
          <h3 className="text-lg font-semibold text-gray-900">Use Case Cost Breakdown</h3>
        </div>
        <div className="flex items-center justify-center h-48 text-gray-500">
          No cost data available for this time period
        </div>
      </div>
    );
  }

  const totalCost = groupedData.reduce((sum, g) => sum + g.totalCost, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
            <h3 className="text-lg font-semibold text-gray-900">Use Case Cost Breakdown</h3>
          </div>
          <span className="text-lg font-bold" style={{ color: BRAND_PRIMARY }}>
            {formatCost(totalCost)}
          </span>
        </div>
      </div>

      <div>
        {(showAll ? groupedData : groupedData.slice(0, INITIAL_DISPLAY_COUNT)).map((group) => {
          const isExpanded = expandedParents.has(group.parent);
          const hasSubSteps =
            group.subSteps.length > 1 ||
            (group.subSteps.length === 1 && group.subSteps[0].sub_step !== null);

          return (
            <div key={group.parent} className="border-b border-gray-50 last:border-b-0">
              <button
                onClick={() => hasSubSteps && toggleExpand(group.parent)}
                className={`w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                  hasSubSteps ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-3">
                  {hasSubSteps ? (
                    isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )
                  ) : (
                    <div className="w-4" />
                  )}
                  <span className="font-medium text-gray-900">
                    {formatUseCaseName(group.parent)}
                  </span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {formatNumber(group.totalQueries)} queries
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCost(group.totalCost)}
                  </span>
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min((group.totalCost / totalCost) * 100, 100)}%`,
                        backgroundColor: BRAND_PRIMARY,
                      }}
                    />
                  </div>
                </div>
              </button>

              {isExpanded && hasSubSteps && (
                <div className="bg-gray-50 px-6 py-2">
                  {group.subSteps.map((step, idx) => (
                    <div
                      key={`${step.parent_usecase}-${step.sub_step}-${idx}`}
                      className="flex items-center justify-between py-2 pl-7 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          {step.sub_step ? formatUseCaseName(step.sub_step) : '(main)'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatNumber(step.query_count)} queries
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-700">{formatCost(step.total_cost)}</span>
                        <span className="text-xs text-gray-500">
                          avg: {formatCost(step.avg_cost)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* See More / Show Less */}
      {groupedData.length > INITIAL_DISPLAY_COUNT && (
        <div className="px-6 py-2 border-t border-gray-100">
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showAll
              ? `Show Less`
              : `See More (${groupedData.length - INITIAL_DISPLAY_COUNT} more use cases)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default UseCaseCostBreakdown;
